import React, { memo, useMemo, useCallback } from 'react';
import moment from 'moment';
import { Button, Tooltip } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { AssetItemDto } from '@api/Api';
import Status from '@components/Approval/Status';
import Loader from '@components/Loader';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import { getMediaDuration, isAudioVideo } from '@helpers/avInfo';
import { formatDuration } from '@helpers/formatDuration';
import { nonNull } from '@helpers/non-null';
import { download } from '@helpers/download';
import { DownloadInvoiceIcon } from '@icons';

type ListViewItemProps = {
  asset: AssetItemDto;
};

export default memo(function ListViewItem({ asset }: ListViewItemProps) {
  const [_, setSearchParams] = useSearchParams();

  const version = useMemo(() => {
    return asset.asset?.versions[0];
  }, [asset.asset?.versions]);

  const isAudioOrVideo = useMemo(() => {
    return isAudioVideo(version?.metadata);
  }, [version]);

  const durationTag = useMemo(() => {
    if (!isAudioVideo(version?.metadata)) return;
    return formatDuration(getMediaDuration(version.metadata));
  }, [version, isAudioOrVideo]);

  const preparePreview = useMemo(() => {
    const res = assetPreviewThumbnails({
      url: version?.metadata.preview?.url || '',
      status: version?.metadata.preview?.status || '',
      type: version?.metadata.type || '',
      format: 'img'
    });

    if (res === 'pending') return 'pending';
    if (typeof res === 'string') return <img src={res || ''} alt="" />;
    return res;
  }, [version]);

  const onAssetClick = useCallback(() => {
    setSearchParams((prev) => {
      prev.set('assetId', asset.id);
      const version = nonNull(asset.asset).versions[0];
      prev.set('versionId', version.id);
      return prev;
    });
  }, [asset, setSearchParams]);
  return (
    <div className="asset-item-wrapper" onClick={onAssetClick}>
      <div className="asset-item-wrapper__preview">
        {preparePreview === 'pending' && (
          <Loader
            classNameWrapper="assets-loader-wrapper"
            spinSize={40}
            isSpinning={true}
          />
        )}
        <div>{preparePreview !== 'pending' && preparePreview}</div>
        {isAudioOrVideo && (
          <div className="asset-item-wrapper__preview--time-tag">
            {durationTag}
          </div>
        )}
        {!!asset.asset?.approvalWorkflow && (
          <Status
            showOverdueTag
            status={asset.asset.approvalWorkflow.status}
            approvalWorkflowStatus={asset.asset.approvalWorkflow.status}
            approvalWorkflowIsPaused={asset.asset.approvalWorkflow.isPaused}
            deadline={asset.asset.approvalWorkflow.deadline}
          />
        )}
      </div>
      <div className="asset-item-wrapper-info">
        <div className="asset-item-wrapper-info__top">
          <Tooltip
            title={`${version?.name}.${version?.extension}`}
            overlayClassName="external_reviewer_tooltip"
          >
            <div className="asset-item-wrapper-info__title">
              {version?.name}.{version?.extension}
            </div>
          </Tooltip>
          {!!asset.asset?.permissions.download && (
            <Button
              type="text"
              size="small"
              className="assets-list-btn-download"
              icon={<DownloadInvoiceIcon />}
              onClick={(e) => {
                e.stopPropagation();
                if (!asset.asset) return;
                const { name, sourceUrl } = asset.asset.versions[0];
                download(sourceUrl, name);
              }}
            />
          )}
        </div>
        <div className="asset-item-wrapper-info__description">
          Edited {moment(asset.modifiedAt).fromNow()}
        </div>
      </div>
    </div>
  );
});
